import { useLayoutEffect } from 'react'

import { getMinTop, VIEWPORT_MARGIN } from './viewportBounds'

/**
 * Fits the window to the screen when it opens:
 * - a tall list caps the window's height to the space left below it (the body
 *   scrolls), moving the window up when even its minimum height does not fit;
 * - once the body scrolls, the window widens by the scrollbar so the content keeps
 *   the width it was designed for (two variation columns), shifting left if it
 *   would overflow the screen.
 * Runs once, so later drags and resizes stay under the user's control, and not at
 * all when the window reopens at a remembered size (that size already includes the
 * scrollbar, so widening it again would grow the window on every reopen).
 * @param {Object} windowRef - Ref to the window element
 * @param {Object} bodyRef - Ref to the window's scrolling body
 * @param {Function} setPosition - Position setter from useDraggable
 * @param {boolean} enabled - Whether the window still needs fitting
 */
function useFitOnOpen(windowRef, bodyRef, setPosition, enabled = true) {
  useLayoutEffect(() => {
    const node = windowRef.current
    const body = bodyRef.current
    if (!enabled || !node || !body) {
      return
    }

    const bottomLimit = window.innerHeight - VIEWPORT_MARGIN
    const { top, left, height, width } = node.getBoundingClientRect()
    let nextTop = top

    if (top + height > bottomLimit) {
      const minHeight = parseFloat(window.getComputedStyle(node).minHeight) || 0
      const fittedHeight = Math.max(minHeight, bottomLimit - top)
      node.style.height = `${fittedHeight}px`

      if (top + fittedHeight > bottomLimit) {
        nextTop = Math.max(getMinTop(), bottomLimit - fittedHeight)
      }
    }

    const scrollbarWidth = body.offsetWidth - body.clientWidth
    const nextWidth = width + scrollbarWidth
    if (scrollbarWidth > 0) {
      node.style.width = `${nextWidth}px`
    }

    const nextLeft = Math.min(left, window.innerWidth - VIEWPORT_MARGIN - nextWidth)
    if (nextTop !== top || nextLeft !== left) {
      setPosition(current => ({
        x: nextLeft !== left ? Math.max(VIEWPORT_MARGIN, nextLeft) : current.x,
        y: nextTop !== top ? nextTop : current.y,
      }))
    }
  }, [windowRef, bodyRef, setPosition, enabled])
}

export default useFitOnOpen
